import React, { useEffect, useRef } from 'react'
import { Bold } from 'lucide-react'

export const TEXT_COLORS = [
  { key: 'noir', hex: '#0f172a', label: 'Noir' },
  { key: 'rouge', hex: '#dc2626', label: 'Rouge' },
  { key: 'orange', hex: '#ea580c', label: 'Orange' },
  { key: 'vert', hex: '#059669', label: 'Vert' },
  { key: 'bleu', hex: '#2563eb', label: 'Bleu' },
  { key: 'violet', hex: '#7c3aed', label: 'Violet' },
]

const ALLOWED_COLORS = new Set(TEXT_COLORS.map(c => c.hex))

function normalizeColor(value) {
  if (!value) return null
  const v = value.trim().toLowerCase()
  if (ALLOWED_COLORS.has(v)) return v
  const m = v.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!m) return null
  const hex = '#' + [m[1], m[2], m[3]].map(n => Number(n).toString(16).padStart(2, '0')).join('')
  return ALLOWED_COLORS.has(hex) ? hex : null
}

// Ne conserve que gras, couleur (palette) et sauts de ligne.
export function sanitizeNoteHtml(html) {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const walk = (node) => {
    let out = ''
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return
      const tag = child.tagName.toLowerCase()
      const inner = walk(child)
      if (tag === 'br') { out += '<br>'; return }
      const bold = tag === 'b' || tag === 'strong' || (child.style && (child.style.fontWeight === 'bold' || Number(child.style.fontWeight) >= 600))
      const color = normalizeColor(child.getAttribute?.('color') || child.style?.color)
      let piece = inner
      if (color) piece = `<span style="color:${color}">${piece}</span>`
      if (bold) piece = `<b>${piece}</b>`
      if (bold || color) { out += piece; return }
      if (tag === 'div' || tag === 'p') { out += (out && !out.endsWith('<br>') ? '<br>' : '') + inner; return }
      out += inner
    })
    return out
  }
  return walk(doc.body.firstChild).replace(/(<br>)+$/, '')
}

export const isRichNote = (text) => /<(b|span|br)\b/i.test(text || '')

export const noteToPlainText = (text) => isRichNote(text)
  ? (new DOMParser().parseFromString(text.replace(/<br\s*\/?>/gi, '\n'), 'text/html').body.textContent || '')
  : (text || '')

export function NoteContent({ contenu, className = '' }) {
  if (!contenu) return null
  if (!isRichNote(contenu)) return <p className={`whitespace-pre-wrap ${className}`}>{contenu}</p>
  return <p className={`whitespace-pre-wrap ${className}`} dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(contenu) }} />
}

export function RichNoteEditor({ value, onChange, onBlur, placeholder = 'Saisir une note...', autoFocus, minRows = 2, testId = 'rich-note-editor', className = '', toolbarClass = '', inputClass = '' }) {
  const ref = useRef(null)
  const lastEmitted = useRef(null)

  useEffect(() => {
    if (!ref.current || value === lastEmitted.current) return
    ref.current.innerHTML = isRichNote(value) ? sanitizeNoteHtml(value) : (value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')
    lastEmitted.current = value
  }, [value])

  useEffect(() => { if (autoFocus) ref.current?.focus() }, [autoFocus])

  const emit = () => {
    const html = sanitizeNoteHtml(ref.current.innerHTML)
    const out = html.replace(/<br>/g, '').trim() === '' ? '' : html
    lastEmitted.current = out
    onChange(out)
    return out
  }

  const exec = (cmd, arg) => {
    ref.current?.focus()
    document.execCommand('styleWithCSS', false, true)
    document.execCommand(cmd, false, arg)
    emit()
  }

  return (
    <div className={`border border-slate-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 ${className}`}>
      <div className={`flex items-center gap-1 px-2 py-1 border-b border-slate-100 ${toolbarClass}`} data-testid={`${testId}-toolbar`}>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('bold')} title="Gras (sélection)"
          className="w-6 h-6 rounded hover:bg-slate-100 text-slate-700 flex items-center justify-center" data-testid={`${testId}-bold`}>
          <Bold size={13} strokeWidth={3} />
        </button>
        <span className="w-px h-4 bg-slate-200 mx-1" />
        {TEXT_COLORS.map(c => (
          <button key={c.key} type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('foreColor', c.hex)} title={`Texte ${c.label}`}
            className="w-4 h-4 rounded-full border border-white ring-1 ring-slate-200 hover:scale-110 transition-transform"
            style={{ backgroundColor: c.hex }} data-testid={`${testId}-color-${c.key}`} />
        ))}
        <span className="ml-auto text-[10px] text-slate-400">Sélectionnez du texte puis cliquez</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { const out = emit(); onBlur?.(out) }}
        data-placeholder={placeholder}
        data-testid={testId}
        className={`rich-note-input px-3 py-2 text-sm outline-none whitespace-pre-wrap break-words ${inputClass || 'text-slate-800'}`}
        style={{ minHeight: `${minRows * 1.5 + 1}rem` }}
      />
    </div>
  )
}
